/**
 * Копия, открытая из файлового менеджера.
 *
 * Во встроенном браузере приложения системный выбор файла запускается не
 * всегда — а перенести данные человеку надо. Поэтому есть обратный путь: он
 * открывает файл копии в любом файловом менеджере, выбирает «Открыть с
 * помощью → Kairo», и приложение забирает файл само.
 *
 * Путь важен тем, что не зависит ни от выбора файла внутри страницы, ни от
 * доступа к общему хранилищу: систему просят не «дай мне файл», а «отдай тот,
 * который человек уже выбрал».
 */

import { App } from '@capacitor/app';
import { Encoding, Filesystem } from '@capacitor/filesystem';

/** Похоже ли на файл копии: по расширению и по имени, которое даёт приложение. */
export function looksLikeBackupUrl(url: string): boolean {
  if (!/^(content|file):/i.test(url)) return false;

  const lower = decodeURIComponent(url).toLowerCase();
  return lower.endsWith('.json') || lower.includes('kairo-backup');
}

/** Читает файл по ссылке, которую дала система. */
export async function readBackupUrl(url: string): Promise<string> {
  const { data } = await Filesystem.readFile({ path: url, encoding: Encoding.UTF8 });
  return typeof data === 'string' ? data : await data.text();
}

/* ─────────── Ожидающая копия ─────────── */

let pending: string | null = null;
const listeners = new Set<(text: string) => void>();

/**
 * Копия пришла в приложение.
 *
 * Если экран копий уже открыт — она уходит ему сразу; если нет, дожидается:
 * приложение при этом могло только что запуститься, и нужный экран ещё не
 * смонтирован.
 */
export function pushIncomingBackup(text: string): void {
  if (listeners.size > 0) {
    for (const listener of listeners) listener(text);
    return;
  }

  pending = text;
}

/** Забирает ожидающую копию: второй раз она не придёт. */
export function takeIncomingBackup(): string | null {
  const text = pending;
  pending = null;
  return text;
}

export function onIncomingBackup(listener: (text: string) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Следит за файлами, открытыми в приложении.
 *
 *Источника два: ссылка, с которой приложение запустили, и открытие файла в уже
 * запущенном приложении. Ошибку чтения прячем: человек мог открыть не тот
 * файл, и падать на этом нельзя — он просто не увидит предложения загрузить.
 */
export function watchIncomingBackups(onFound: (text: string) => void): () => void {
  let active = true;

  const handle = async (url: string | undefined) => {
    if (!active || !url || !looksLikeBackupUrl(url)) return;

    try {
      onFound(await readBackupUrl(url));
    } catch {
      // Файл не прочитался — молчим: это не сбой приложения.
    }
  };

  void App.getLaunchUrl().then((result) => handle(result?.url));
  const listener = App.addListener('appUrlOpen', (event) => void handle(event.url));

  return () => {
    active = false;
    void listener.then((subscription) => subscription.remove());
  };
}
